import { Outlet, NavLink } from 'react-router-dom';
import {
  Phone,
  MessageSquare,
  MapPin,
  Package,
  ClipboardCheck,
  Network,
  AlertTriangle,
  Wifi,
  WifiOff,
  Menu,
  X,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';

const navItems = [
  { path: '/', icon: AlertTriangle, label: 'Dashboard' },
  { path: '/contacts', icon: Phone, label: 'Contacts' },
  { path: '/messages', icon: MessageSquare, label: 'Messages' },
  { path: '/reunion', icon: MapPin, label: 'Reunion' },
  { path: '/resources', icon: Package, label: 'Resources' },
  { path: '/checklist', icon: ClipboardCheck, label: 'Checklist' },
  { path: '/mesh', icon: Network, label: 'Mesh' },
];

export function Layout() {
  const { isOnline, pendingMessages, notifications, removeNotification, isSidebarOpen, setSidebarOpen } = useApp();

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Offline Banner */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="bg-warning-600 text-black px-4 py-2 flex items-center justify-center gap-2 font-medium text-sm"
          >
            <WifiOff className="w-4 h-4" />
            <span>Offline Mode - Messages will be queued</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 rounded-lg emergency-gradient flex items-center justify-center">
                  <Network className="w-6 h-6 text-white" />
                </div>
                {isOnline && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-safe-500 rounded-full border-2 border-gray-800" />
                )}
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">DisasterMesh</h1>
                <p className="text-xs text-gray-400">Emergency Coordination</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {pendingMessages > 0 && (
              <div className="badge-warning flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                <span>{pendingMessages} pending</span>
              </div>
            )}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isOnline ? 'bg-safe-900/50 text-safe-300' : 'bg-warning-900/50 text-warning-300'
            }`}>
              {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              <span>{isOnline ? 'Online' : 'Offline'}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex w-20 bg-gray-800 border-r border-gray-700 flex-col items-center py-4 gap-2">
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center w-16 h-16 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-emergency-600/20 text-emergency-400'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`
              }
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">{label}</span>
            </NavLink>
          ))}
        </aside>

        {/* Mobile Sidebar */}
        <AnimatePresence>
          {isSidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden fixed inset-0 bg-black/50 z-40"
              />
              <motion.aside
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-gray-800 border-r border-gray-700 z-50 flex flex-col"
              >
                <div className="p-4 border-b border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg emergency-gradient flex items-center justify-center">
                      <Network className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-white">DisasterMesh</h1>
                      <p className="text-xs text-gray-400">Emergency Coordination</p>
                    </div>
                  </div>
                </div>
                <nav className="flex-1 p-4 space-y-2">
                  {navItems.map(({ path, icon: Icon, label }) => (
                    <NavLink
                      key={path}
                      to={path}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                          isActive
                            ? 'bg-emergency-600/20 text-emergency-400'
                            : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`
                      }
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{label}</span>
                    </NavLink>
                  ))}
                </nav>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 lg:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom Navigation - Mobile */}
      <nav className="lg:hidden bg-gray-800 border-t border-gray-700 px-2 py-1 safe-area-inset-bottom">
        <div className="flex justify-around">
          {navItems.slice(0, 5).map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all duration-200 ${
                  isActive ? 'text-emergency-400' : 'text-gray-400'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs mt-1">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Notifications */}
      <div className="fixed top-20 right-4 z-50 space-y-2 max-w-sm w-full pointer-events-none">
        <AnimatePresence>
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              className={`pointer-events-auto rounded-lg p-4 shadow-lg border ${
                notification.type === 'success'
                  ? 'bg-safe-900/90 border-safe-700 text-safe-100'
                  : notification.type === 'warning'
                  ? 'bg-warning-900/90 border-warning-700 text-warning-100'
                  : notification.type === 'error'
                  ? 'bg-emergency-900/90 border-emergency-700 text-emergency-100'
                  : 'bg-gray-800/90 border-gray-700 text-gray-100'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-semibold">{notification.title}</h4>
                  <p className="text-sm opacity-90">{notification.message}</p>
                </div>
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="p-1 hover:opacity-70 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
